
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Search, Globe, BookOpen, Plus, Loader2,
  X, AlertCircle, Sparkles, RefreshCw,
  ChevronLeft, ChevronRight, Sun, Moon,
  LayoutGrid, List as ListIcon, MoreHorizontal, ChevronDown,
  Globe2, FileText, Zap, FileCode, ChevronUp, BookMarked, Headphones, Library
} from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { storageService } from '../services/storageService';
import { fileStorageService } from '../services/fileStorageService';
import { Book, ViewMode } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { useData } from '../contexts/DataContext';
import { supabase } from '../services/supabaseClient';
import { dbManager } from '../services/db_manager';
import { cacheManager } from '../services/cacheManager';
import { libraryService } from '../services/libraryService';
import { getLanguageName } from '../services/languageConstants';
import BookDetailsModal from './BookDetailsModal';
import { WatchdogGuard } from '../components/WatchdogGuard';
import { AppBackButton } from '../components/AppBackButton';
import { AnimatedIcon } from '../components/AnimatedIcon';
import { BookCoverArt } from '../components/BookCoverArt';
import { StudyPageHeader } from '../components/StudyPageHeader';
import { BookShelfSkeleton } from '../components/SkeletonLoader';
import { tourService } from '../services/tourService';
import { fetchRatingSummaries, RatingSummary } from '../services/ratingService';
import { RatingBadge } from '../components/RatingStars';

const ITEMS_PER_PAGE = 30;
const PAGES_PER_WINDOW = 5;
const LIBRARY_HTML_URL = "https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/library-capas-description-level-tags.json";
const LIBRARY_PDF_URL = "https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/library-pdfs-corrigido.json";
const R2_HTML_BASE = "https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/biblioteca-online-htmls/";
const R2_COVER_BASE = "https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/capas-atualizadas/";
const R2_PDF_BASE = "https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/pdfs/";
const COMMENT_BOOKS_URL = "https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/final-comment-books-and-no-commented-books.json";

export interface CustomCatalogBook {
  id: string;
  title: string;
  author: string;
  language: string;
  url: string;
  cover: string;
  file: string;
  format: 'epub' | 'txt' | 'html' | 'pdf';
  subjects?: string[];
  description?: string;
  reading_level?: string | number;
  downloads?: number;
}

const ISO_FLAG_MAP: Record<string, string> = {
  'af': '🇿🇦', 'ar': '🇸🇦', 'arp': '🏹', 'bg': '🇧🇬', 'bo': '☸️', 'br': '⚓',
  'ca': '🏔️', 'ceb': '🇵🇭', 'cs': '🇨🇿', 'cy': '🏴󠁧󠁢󠁷󠁬ês󠁿', 'da': '🇩🇰', 'de': '🇩🇪',
  'el': '🇬🇷', 'en': '🇺🇸', 'enm': '📜', 'eo': '🟢', 'es': '🇪🇸', 'et': '🇪🇪',
  'fa': '🇮🇷', 'fi': '🇫🇮', 'fr': '🇫🇷', 'fur': '⛰️', 'fy': '🇳🇱', 'ga': '🇮🇪',
  'gl': '🌾', 'gla': '🏴󠁧󠁢󠁳󠁣󠁴󠁿', 'he': '🇮🇱', 'hu': '🇭🇺', 'ia': '🌐', 'ilo': '🇵🇭',
  'is': '🇮🇸', 'it': '🇮🇹', 'iu': '❄️', 'ja': '🇯🇵', 'la': '🏛️', 'mi': '🌀',
  'myn': '🗿', 'na': '🇳🇷', 'nah': '🌋', 'nap': '🍕', 'nl': '🇳🇱', 'no': '🇳🇴',
  'oc': '🌿', 'oj': '🌲', 'pl': '🇵🇱', 'pt': '🇧🇷', 'rmq': '💃', 'ro': '🇷🇴',
  'ru': '🇷🇺', 'sa': '🕉️', 'sco': '🏴󠁧󠁢󠁳󠁣󠁴󠁿', 'sl': '🇸🇮', 'sr': '🇷🇸', 'sv': '🇸🇪',
  'te': '🇮🇳', 'tl': '🇵🇭', 'yi': '🕎', 'zh': '🇨🇳', 'und': '❓'
};

const SmartBookItem: React.FC<{
  book: CustomCatalogBook;
  viewMode: ViewMode;
  onClick: (book: CustomCatalogBook) => void;
  rating?: RatingSummary;
}> = ({ book, viewMode, onClick, rating }) => {
  return (
    <div
      onClick={() => onClick(book)}
      className={viewMode === 'grid'
        ? "group relative cursor-pointer transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-1.5"
        : "flex items-center gap-5 p-3 pr-5 bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-[24px] transition-colors hover:bg-tint-stone cursor-pointer group relative overflow-hidden"
      }
    >
      <div className={viewMode === 'grid'
        ? "aspect-[2/3] rounded-[22px] overflow-hidden transition-shadow duration-500 group-hover:shadow-[0_24px_50px_-28px_rgba(0,0,0,0.55)]"
        : "w-16 h-24 rounded-[14px] overflow-hidden flex-shrink-0 relative"}>
        <BookCover book={book} isSmall={viewMode === 'list'} />
      </div>
      {viewMode === 'grid' ? (
        <div className="mt-3 px-1">
          <h4 className="text-[13px] font-medium text-ink leading-snug line-clamp-2">{book.title}</h4>
          {book.author && <p className="text-[11px] text-muted mt-0.5 line-clamp-1">{book.author}</p>}
          {rating && <RatingBadge avg={rating.avg} count={rating.count} className="mt-1" />}
        </div>
      ) : (
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-lg font-medium tracking-[-0.02em] text-ink truncate">{book.title}</h3>
          <p className="text-muted text-sm mt-0.5 truncate">{book.author}</p>
          {rating && <RatingBadge avg={rating.avg} count={rating.count} className="mt-1" />}
        </div>
      )}
    </div>
  );
};

const BookCover: React.FC<{ book: { id: string, title: string, author?: string, language?: string, cover?: string, format?: string }, isSmall?: boolean }> = ({ book, isSmall = false }) => {
  const [hasError, setHasError] = useState(false);
  const [retryWithR2, setRetryWithR2] = useState(false);
  const { isDarkMode } = useTheme();

  let currentCoverUrl = "";
  if (!retryWithR2 && book.cover && book.cover.startsWith("http")) {
    currentCoverUrl = book.cover;
  } else if (book.id) {
    currentCoverUrl = `${R2_COVER_BASE}${book.id}.jpg`;
  }

  // Design 2.0: mesma cadeia de capas (URL original -> R2 -> capa gerada), visual compartilhado com a estante
  return (
    <BookCoverArt
      src={currentCoverUrl && !hasError ? currentCoverUrl : undefined}
      title={book.title}
      author={book.author}
      isSmall={isSmall}
      onError={() => {
        if (!retryWithR2 && book.cover && book.cover.startsWith("http")) {
          setRetryWithR2(true);
        } else {
          setHasError(true);
        }
      }}
    />
  );
};

const FilterModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  languages: { code: string, name: string }[];
  selectedLang: string;
  onSelectLang: (code: string) => void;
  selectedLevel: string;
  onSelectLevel: (level: string) => void;
  sortBy: 'mais_lidos' | 'menos_lidos' | 'relevancia';
  onSelectSort: (sort: 'mais_lidos' | 'menos_lidos' | 'relevancia') => void;
  workType: '' | 'original' | 'commented';
  onSelectWorkType: (type: '' | 'original' | 'commented') => void;
}> = ({ isOpen, onClose, languages, selectedLang, onSelectLang, selectedLevel, onSelectLevel, sortBy, onSelectSort, workType, onSelectWorkType }) => {
  const { t } = useTranslation();
  const [openSection, setOpenSection] = useState<string | null>(null);

  if (!isOpen) return null;

  const toggleSection = (section: string) => {
    setOpenSection(prev => prev === section ? null : section);
  };

  const levels = [
    { id: '', label: t('online_library.all_levels') },
    { id: 'A1', label: `${t('online_library.level')} A1` },
    { id: 'A2', label: `${t('online_library.level')} A2` },
    { id: 'B1', label: `${t('online_library.level')} B1` },
    { id: 'B2', label: `${t('online_library.level')} B2` },
    { id: 'C1', label: `${t('online_library.level')} C1` },
    { id: 'C2', label: `${t('online_library.level')} C2` }
  ];

  const sortOptions = [
    { id: 'relevancia', label: t('online_library.most_relevant') },
    { id: 'mais_lidos', label: t('online_library.most_read') },
    { id: 'menos_lidos', label: t('online_library.least_read') }
  ];

  const workOptions = [
    { id: '', label: t('online_library.all_works') },
    { id: 'original', label: t('online_library.originals') },
    { id: 'commented', label: t('online_library.commented') }
  ];

  const getSelectedLabel = (section: string) => {
    if (section === 'lang') return selectedLang ? languages.find(l => l.code === selectedLang)?.name || selectedLang : t('online_library.all_languages');
    if (section === 'level') return selectedLevel ? levels.find(l => l.id === selectedLevel)?.label || selectedLevel : t('online_library.all_levels');
    if (section === 'sort') return sortOptions.find(s => s.id === sortBy)?.label || '';
    if (section === 'work') return workOptions.find(w => w.id === workType)?.label || '';
    return '';
  };

  const sections = [
    { key: 'lang', icon: <Globe size={16} />, title: t('online_library.languages') },
    { key: 'level', icon: <Sparkles size={16} />, title: t('online_library.difficulty') },
    { key: 'sort', icon: <RefreshCw size={16} />, title: t('online_library.sort_by') },
    { key: 'work', icon: <BookMarked size={16} />, title: t('online_library.work_type') }
  ];

  const renderOptions = (section: string) => {
    if (section === 'lang') {
      const allLangs = [{ code: '', name: t('online_library.all_languages') }, ...languages];
      return allLangs.map(lang => (
        <button key={lang.code} onClick={() => onSelectLang(lang.code)}
          className={`w-full text-left px-5 py-3 rounded-2xl text-xs font-bold transition-all ${selectedLang === lang.code ? 'bg-ink text-surface' : 'hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-500'}`}
        >{lang.name}</button>
      ));
    }
    if (section === 'level') {
      return levels.map(lvl => (
        <button key={lvl.id} onClick={() => onSelectLevel(lvl.id)}
          className={`w-full text-left px-5 py-3 rounded-2xl text-xs font-bold transition-all ${selectedLevel === lvl.id ? 'bg-ink text-surface' : 'hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-500'}`}
        >{lvl.label}</button>
      ));
    }
    if (section === 'sort') {
      return sortOptions.map(sort => (
        <button key={sort.id} onClick={() => onSelectSort(sort.id as any)}
          className={`w-full text-left px-5 py-3 rounded-2xl text-xs font-bold transition-all ${sortBy === sort.id ? 'bg-ink text-surface' : 'hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-500'}`}
        >{sort.label}</button>
      ));
    }
    if (section === 'work') {
      return workOptions.map(w => (
        <button key={w.id} onClick={() => onSelectWorkType(w.id as any)}
          className={`w-full text-left px-5 py-3 rounded-2xl text-xs font-bold transition-all ${workType === w.id ? 'bg-ink text-surface' : 'hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-500'}`}
        >{w.label}</button>
      ));
    }
    return null;
  };

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 sm:p-6 bg-slate-950/60 backdrop-blur-md" onClick={onClose}>
      <div className="bg-surface w-full max-w-lg rounded-[28px] overflow-hidden flex flex-col max-h-[85vh] animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
        <div className="p-6 md:p-8 border-b border-black/[0.06] dark:border-white/[0.06] flex items-center justify-between shrink-0">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary/10 text-primary rounded-2xl"><Zap size={24} /></div>
            <div>
              <h3 className="text-xl font-medium text-ink tracking-[-0.02em]">{t('online_library.filters_title')}</h3>
              <p className="text-[10px] font-normal text-slate-400 uppercase font-mono tracking-[0.14em]">{t('online_library.filters_subtitle')}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-3 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors text-slate-400"><X size={24} /></button>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
          {sections.map(section => (
            <div key={section.key} className="border-b border-black/[0.06] dark:border-white/[0.06] last:border-b-0">
              <button
                onClick={() => toggleSection(section.key)}
                className="w-full flex items-center justify-between px-6 md:px-8 py-5 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-primary">{section.icon}</span>
                  <span className="font-semibold text-sm dark:text-ink">{section.title}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[11px] font-bold text-primary bg-primary/10 px-3 py-1 rounded-full truncate max-w-[140px]">
                    {getSelectedLabel(section.key)}
                  </span>
                  {openSection === section.key ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-ink/60" />}
                </div>
              </button>
              {openSection === section.key && (
                <div className="px-6 md:px-8 pb-5 max-h-[240px] overflow-y-auto custom-scrollbar space-y-1" style={{ WebkitOverflowScrolling: 'touch' }}>
                  {renderOptions(section.key)}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="p-6 md:p-8 bg-slate-50 dark:bg-slate-800/50 border-t border-black/[0.06] dark:border-white/[0.06] shrink-0">
          <button onClick={onClose} className="text-[13px] font-medium w-full h-12 bg-primary text-slate-900 rounded-3xl hover:opacity-90 active:scale-95 transition-all">
            {t('online_library.apply_filters')}
          </button>
        </div>
      </div>
    </div>
  );
};

const LibriTuiLibraryPage: React.FC = () => {
  const navigate = useNavigate();
  const { isDarkMode, toggleAppOnlyTheme } = useTheme();
  const { user } = useAuth();
  const { t } = useTranslation();
  const { refreshBooks, setBooks } = useData();

  const [searchText, setSearchText] = useState('');
  const [searchQuery, setSearchQuery] = useState(''); // Debounced query
  const [query, setQuery] = useState(''); // Language code filter
  const [selectedLanguageName, setSelectedLanguageName] = useState(''); // Armazena o nome em Português
  const [activeTab, setActiveTab] = useState<'pdf' | 'html'>('html');
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const [allBooks, setAllBooks] = useState<CustomCatalogBook[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedBook, setSelectedBook] = useState<CustomCatalogBook | null>(null);
  const [isImporting, setIsImporting] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const [viewMode, setViewMode] = useState<ViewMode>(() => (localStorage.getItem('viewMode') as ViewMode) || 'grid');
  const [selectedLevel, setSelectedLevel] = useState('');
  const [sortBy, setSortBy] = useState<'mais_lidos' | 'menos_lidos' | 'relevancia'>('relevancia');
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [workType, setWorkType] = useState<'' | 'original' | 'commented'>('');
  const [commentBooksMap, setCommentBooksMap] = useState<Map<string, string>>(new Map());
  
  const [currentPage, setCurrentPage] = useState(1);
  const [maxVisiblePage, setMaxVisiblePage] = useState(PAGES_PER_WINDOW);
  const [isDataLoading, setIsDataLoading] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) setIsLangDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 500ms Debounce for Search Query
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchText);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchText]);

  const handleSetViewMode = (mode: ViewMode) => {
    setViewMode(mode);
    localStorage.setItem('viewMode', mode);
  };

  const fetchCatalog = useCallback(async (force: boolean = false) => {
    setLoading(true);
    setError(null);
    if (force) setAllBooks([]);

    const fetcher = async () => {
      const timestamp = new Date().getTime();
      const responseHtml = await fetch(`${LIBRARY_HTML_URL}?v=${timestamp}`, {
        mode: 'cors',
        headers: { 'Accept': 'application/json' }
      });
      if (!responseHtml.ok) throw new Error(`Erro HTTP HTML: ${responseHtml.status}`);
      const rawText = await responseHtml.text();
      const cleanText = rawText.replace(/: NaN/g, ': null');
      return JSON.parse(cleanText);
    };

    const handleData = (data: any[]) => {
      const normalize = (list: any[], defaultFormat: 'html' | 'pdf') => {
        return (list || []).map((item: any) => {
          try {
            const id = String(item.id || item.gutenberg_id || item.ID || "").trim();
            if (!id || id === "undefined") return null;

            const format = (item.format || defaultFormat).toLowerCase();
            const fileUrl = format === 'html'
              ? `${R2_HTML_BASE}${id}.html`
              : `${R2_PDF_BASE}${id}.pdf`;

            const rawLang = String(item.language || item.idioma || "und").trim();
            const normalizedLang = rawLang.split(/[;,\s]/)[0].toLowerCase();

            let cover = String(item.cover || item.capa || "").trim();
            if (cover.toLowerCase() === "vazio") cover = "";

            return {
              id,
              title: item.title || item.titulo || "Título Desconhecido",
              author: item.author || item.autor || "Autor Desconhecido",
              language: normalizedLang,
              url: fileUrl,
              file: fileUrl,
              cover: cover,
              format: defaultFormat,
              description: item.description || item.descricao || "",
              reading_level: item.reading_level,
              subjects: Array.isArray(item.subject) ? item.subject : (item.subject ? [item.subject] : []),
              downloads: parseInt(item.downloads) || 0
            };
          } catch (e) {
            return null;
          }
        }).filter(b => b !== null && b.url);
      };

      const normalized = normalize(data, 'html');
      setAllBooks(normalized);
      setLoading(false);
      
      // Cache first page for instant loading
      if (normalized.length > 0) {
        cacheManager.set('online_library_first_page_v2', normalized.slice(0, 100)); // ~2 pages worth
      }
    };

    try {
      if (force) {
        const data = await fetcher();
        handleData(data);
        cacheManager.set('online_library_metadata_v2', data);
      } else {
        await cacheManager.swr('online_library_metadata_v2', fetcher, handleData, 3600000); // 1 hour TTL
      }
    } catch (err: any) {
      console.error("Erro crítico no fetch:", err);
      setError("Não foi possível carregar a lista de livros.");
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const handleTour = () => {
      if (tourService.isActive() && tourService.getPhase() === 'LIBRARY' && !loading) {
        tourService.createDriver([
          {
            element: '#tour-library-search',
            popover: {
              title: 'Busca Inteligente',
              description: 'Filtre por idioma, nível de dificuldade e pesquise por clássicos da literatura.',
              doneBtnText: 'Próximo',
              nextBtnText: 'Próximo'
            }
          },
          {
            element: '#tour-first-book',
            popover: {
              title: 'Escolha um Livro',
              description: 'Clique neste livro para ver os detalhes.',
              showButtons: [],
            },
            onHighlighted: (el) => {
               if (el) el.style.pointerEvents = 'auto';
            }
          }
        ]);
      }
    };
    
    setTimeout(handleTour, 800);
    window.addEventListener('libritui-tour-started', handleTour);
    return () => window.removeEventListener('libritui-tour-started', handleTour);
  }, [loading]);

  useEffect(() => {
    if (selectedBook) {
      if (tourService.isActive() && tourService.getPhase() === 'LIBRARY') {
         setTimeout(() => {
            tourService.createDriver([
              {
                element: '#tour-import-btn',
                popover: {
                  title: 'Adicionar à Estante',
                  description: 'Clique em "Salvar na Estante" para começar a leitura e transformar vocabulário em fluência!',
                  showButtons: [],
                },
                onHighlighted: (el) => {
                   if (el) el.style.pointerEvents = 'auto';
                }
              }
            ]);
         }, 500);
      }
    }
  }, [selectedBook]);

  // Instant Loading: render cached first page immediately
  useEffect(() => {
    const cachedFirstPage = cacheManager.get<CustomCatalogBook[]>('online_library_first_page_v2');
    if (cachedFirstPage && cachedFirstPage.length > 0) {
      setAllBooks(cachedFirstPage);
      setLoading(false);
    }
    fetchCatalog();
  }, [fetchCatalog]);

  // Fetch comment-books classification from R2 (cached with 10MB limit)
  useEffect(() => {
    const cached = cacheManager.get<{id: number, type: string}[]>('comment_books_index');
    if (cached) {
      const map = new Map<string, string>();
      cached.forEach((item: any) => map.set(String(item.id), item.type));
      setCommentBooksMap(map);
    }
    const fetchCommentBooks = async () => {
      try {
        const res = await fetch(`${COMMENT_BOOKS_URL}?v=${Date.now()}`, { mode: 'cors', headers: { 'Accept': 'application/json' } });
        if (!res.ok) return;
        const text = await res.text();
        const data = JSON.parse(text.replace(/^\uFEFF/, ''));
        cacheManager.setWithLimit('comment_books_index', data);
        const map = new Map<string, string>();
        data.forEach((item: any) => map.set(String(item.id), item.type));
        setCommentBooksMap(map);
      } catch (e) {
        console.warn('[LibriTui] Failed to fetch comment-books index:', e);
      }
    };
    fetchCommentBooks();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    setMaxVisiblePage(PAGES_PER_WINDOW);
  }, [searchQuery, selectedLanguageName, activeTab, workType]);
  const availableLanguages = useMemo(() => {
    const counts = new Map<string, number>();
    allBooks.forEach(b => {
      if (b.language) {
        counts.set(b.language, (counts.get(b.language) || 0) + 1);
      }
    });

    const POPULAR_ORDER = ['en', 'fr', 'es', 'pt', 'de', 'it', 'ja', 'zh', 'ru', 'nl', 'hu'];
    const getRank = (code: string) => {
      const idx = POPULAR_ORDER.indexOf(code.toLowerCase());
      return idx === -1 ? 999 : idx;
    };

    return Array.from(counts.keys()).sort((a, b) => {
      const rankA = getRank(a);
      const rankB = getRank(b);
      if (rankA !== rankB) return rankA - rankB;
      const countA = counts.get(a) || 0;
      const countB = counts.get(b) || 0;
      return countB - countA;
    });
  }, [allBooks]);

  const dynamicLanguages = useMemo(() => {
    return availableLanguages.map(code => getLanguageName(code, t));
  }, [availableLanguages]);

  const filteredBooks = useMemo(() => {
    let result = allBooks.filter(b => b.format === activeTab);

    if (query) {
      result = result.filter(b => b.language === query);
    }

    if (selectedLevel) {
      result = result.filter(b => libraryService.mapReadingLevelToCefr(b.reading_level).includes(selectedLevel));
    }

    if (searchQuery.trim()) {
      const lowQ = searchQuery.toLowerCase();
      result = result.filter(b =>
        (b.title && b.title.toLowerCase().includes(lowQ)) ||
        (b.author && b.author.toLowerCase().includes(lowQ))
      );
    }

    // Work type filter (original / commented)
    if (workType && commentBooksMap.size > 0) {
      result = result.filter(b => commentBooksMap.get(b.id) === workType);
    }

    if (sortBy === 'mais_lidos') {
      result = [...result].sort((a, b) => (b.downloads || 0) - (a.downloads || 0));
    } else if (sortBy === 'menos_lidos') {
      result = [...result].sort((a, b) => (a.downloads || 0) - (b.downloads || 0));
    }

    return result;
  }, [allBooks, searchQuery, query, selectedLevel, sortBy, activeTab, workType, commentBooksMap]);

  const handlePageChange = (page: number) => {
    if (page > maxVisiblePage) {
      setIsDataLoading(true);
      // Simulating loading next chunk of data as requested
      setTimeout(() => {
        setMaxVisiblePage(prev => prev + PAGES_PER_WINDOW);
        setCurrentPage(page);
        setIsDataLoading(false);
      }, 500);
    } else {
      setCurrentPage(page);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getWindowedPageNumbers = () => {
    const currentWindowStart = Math.floor((currentPage - 1) / PAGES_PER_WINDOW) * PAGES_PER_WINDOW + 1;
    const pages = [];
    for (let i = currentWindowStart; i < currentWindowStart + PAGES_PER_WINDOW; i++) {
      if (i <= Math.ceil(filteredBooks.length / ITEMS_PER_PAGE)) {
        pages.push(i);
      }
    }
    return pages;
  };

  const handleBookClick = (book: CustomCatalogBook) => {
    setSelectedBook(book);
  };

  const handleImport = async (cBook: CustomCatalogBook) => {
    setIsImporting(true);
    try {
      const bookId = cBook.id; // stable numeric ID
      const mimeType = cBook.format === 'html' ? 'text/html' : 'application/pdf';

      if (cBook.format === 'html') {
        storageService.saveBook({
          id: bookId, title: cBook.title, author: cBook.author,
          coverUrl: cBook.cover || `generated://${bookId}`,
          fileUrl: cBook.file,
          lastPage: 1, totalPages: 0, dateAdded: Date.now(),
          category: `LibriTui Library (HTML)`, isFavorite: false, language: cBook.language,
          format: 'html',
          description: cBook.description,
          reading_level: cBook.reading_level,
          subjects: cBook.subjects
        });
      } else {
        const response = await fetch(cBook.file, { mode: 'cors' });
        if (!response.ok) throw new Error("Arquivo não disponível via rede.");
        const blob = await response.blob();
        await fileStorageService.saveFile(bookId, blob);

        storageService.saveBook({
          id: bookId, title: cBook.title, author: cBook.author,
          coverUrl: cBook.cover || `generated://${bookId}`,
          fileUrl: `indexeddb://${bookId}`, lastPage: 1, totalPages: 0, dateAdded: Date.now(),
          category: `LibriTui Library (PDF)`, isFavorite: false, language: cBook.language,
          format: 'pdf',
          description: cBook.description,
          reading_level: cBook.reading_level,
          subjects: cBook.subjects
        });
      }

      localStorage.setItem(`mime_${bookId}`, mimeType);

      if (user) {
        if (!user.id) {
          console.warn("Usuário sem ID, abortando sync com D1.");
          return;
        }
        console.log("Sincronizando livro da biblioteca com D1...");
        try {
          await dbManager.from('books', user.id).upsert({
            id: String(bookId),       // Fix: Ensure id is sent and is the R2 key
            custom_id: String(bookId),
            OR_ID_CUSTOM_ID: String(bookId), // Fix: Mandatory field
            title: cBook.title,
            author: cBook.author,
            file_url: cBook.format === 'html' ? cBook.file : `indexeddb://${bookId}`,
            cover_url: cBook.cover || `generated://${bookId}`,
            last_page: 1,
            user_id: user.id,
            file_type: cBook.format === 'html' ? 'html' : 'pdf',
            language: cBook.language,
            category: cBook.format === 'html' ? 'LibriTui Library (HTML)' : 'LibriTui Library (PDF)',
            display_order: Date.now(), // Fix: Mandatory field
            description: cBook.description,
            reading_level: String(cBook.reading_level || ""),
            subjects: JSON.stringify(cBook.subjects || [])
          });
        } catch (error: any) {
          console.error("Erro ao sincronizar com D1:", { message: error.message });
        }
      }

      // Refresh global books state so the new book appears instantly in My Library
      await refreshBooks(true);

      if (tourService.isActive() && tourService.getPhase() === 'LIBRARY') {
        tourService.setPhase('READER');
        tourService.stopTour();
      }

      setSelectedBook(null);
      navigate(`/reader/${bookId}`);
    } catch (err: any) {
      console.error("Erro na importação:", err);
      alert(`Erro: Falha na comunicação com o servidor de arquivos.`);
    } finally {
      setIsImporting(false);
    }
  };

  const handlePreview = (b: CustomCatalogBook) => {
    if (b.format === 'html') {
      window.open(b.file, '_blank');
    } else {
      navigate(`/preview/${b.id}`, { state: { book: b } });
    }
    setSelectedBook(null);
  };

  const currentBooks = useMemo(() => {
    return filteredBooks.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  }, [filteredBooks, currentPage]);

  // Notas estimadas só dos livros da página (um pedido em lote, com cache de borda curto)
  const [ratingSummaries, setRatingSummaries] = useState<Record<string, RatingSummary>>({});
  const currentIdsKey = currentBooks.map(b => b.id).join(',');
  useEffect(() => {
    if (!currentIdsKey) return;
    let alive = true;
    fetchRatingSummaries('book', currentIdsKey.split(',')).then(r => {
      if (alive) setRatingSummaries(prev => ({ ...prev, ...r }));
    });
    return () => { alive = false; };
  }, [currentIdsKey]);

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500 pb-24 px-4 md:px-0">
      <div className="space-y-4">
        <AppBackButton fallbackTo="/" />
        <StudyPageHeader
          tourKey="online_library"
          title={t('library.onlineLib')}
          actions={
            <span data-tour="online-links" className="flex items-center gap-2">
              <Link to="/library" className="w-10 h-10 rounded-full bg-surface border border-black/[0.06] dark:border-white/[0.06] text-ink hover:bg-tint-stone flex items-center justify-center transition-colors" title="Minha Estante">
                <AnimatedIcon motion="flip"><Library size={17} strokeWidth={1.75} /></AnimatedIcon>
              </Link>
              <Link to="/audiobooks" className="w-10 h-10 rounded-full bg-surface border border-black/[0.06] dark:border-white/[0.06] text-ink hover:bg-tint-stone flex items-center justify-center transition-colors" title="Audiobooks">
                <AnimatedIcon motion="bounce"><Headphones size={17} strokeWidth={1.75} /></AnimatedIcon>
              </Link>
            </span>
          }
        />
        <p className="inline-flex items-center gap-2 h-8 px-3.5 rounded-full bg-tint-butter text-ink font-mono text-[10px] uppercase tracking-[0.14em] tabular-nums">
          <Zap size={11} strokeWidth={1.75} />
          {allBooks.length.toLocaleString()} {t('online_library.synced_titles')}
        </p>
      </div>

      <div className="flex flex-col md:flex-row gap-4 items-center w-full">
        <div className="flex-1 relative group w-full">
          <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-muted" size={18} strokeWidth={1.75} />
          <input
            id="tour-library-search"
            type="text" placeholder={t('online_library.search_placeholder')} value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="w-full h-14 bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-full pl-[52px] pr-6 text-[15px] text-ink placeholder:text-muted outline-none transition-colors focus:border-ink/25"
          />
        </div>
        <div className="relative w-full md:w-72">
          <button
            onClick={() => setIsFilterModalOpen(true)}
            data-tour="online-filter" className="group w-full h-14 flex items-center justify-between bg-tint-sky text-ink rounded-full px-6 font-medium hover:opacity-90 transition-opacity"
          >
            <div className="flex items-center gap-3">
              <Globe className="group-hover:rotate-12 transition-transform duration-500" size={17} strokeWidth={1.75} />
              <span className="truncate text-sm">
                {query ? getLanguageName(query, t) : t('online_library.filters')}
              </span>
            </div>
            <ChevronDown size={16} className="text-slate-400" />
          </button>
        </div>
      </div>

      <WatchdogGuard isLoading={loading || isDataLoading} onRetry={() => fetchCatalog(true)}>
        {loading || isDataLoading ? (
          <BookShelfSkeleton />
        ) : error ? (
          <div className="px-8 py-16 sm:p-20 text-center bg-tint-rose text-ink rounded-[32px]">
            <AlertCircle className="mx-auto mb-4 text-bad" size={48} />
            <h3 className="text-xl font-medium text-ink tracking-[-0.02em]">{error}</h3>
            <button onClick={() => fetchCatalog(true)} className="mt-6 h-11 px-6 bg-primary text-slate-900 rounded-full text-sm font-medium">{t('online_library.retry')}</button>
          </div>
        ) : filteredBooks.length === 0 ? (
          <div className="px-8 py-16 sm:p-20 text-center bg-surface rounded-[32px] border border-black/[0.06] dark:border-white/[0.06]">
            <h3 className="font-display text-2xl font-light tracking-[-0.04em] text-ink">{t('online_library.no_results')}</h3>
            <p className="text-muted text-sm mt-2">{t('online_library.no_results_hint')}</p>
          </div>
        ) : (
          <div className="space-y-16">
            <div data-tour="online-books" className={viewMode === 'grid' ? "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-10" : "space-y-4"}>
              {currentBooks.map((book, i) => (
                <div key={book.id} id={i === 0 ? "tour-first-book" : undefined}>
                  <SmartBookItem book={book} viewMode={viewMode} onClick={handleBookClick} rating={ratingSummaries[String(book.id)]} />
                </div>
              ))}
            </div>

          {filteredBooks.length > ITEMS_PER_PAGE && (
            <div className="flex justify-center items-center gap-3 pt-12 border-t border-black/[0.06] dark:border-white/[0.06]">
              <button
                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="w-12 h-12 flex items-center justify-center bg-primary/20 dark:bg-primary/30 text-primary border border-primary/20 rounded-2xl disabled:opacity-20 disabled:cursor-not-allowed hover:bg-primary/40 transition-all"
              >
                <ChevronLeft size={20} />
              </button>

              <div className="flex items-center gap-2">
                {getWindowedPageNumbers().map((p) => (
                  <button
                    key={p}
                    onClick={() => handlePageChange(p)}
                    className={`w-12 h-12 rounded-2xl font-semibold text-sm transition-all active:scale-95 flex items-center justify-center ${currentPage === p
                      ? 'bg-ink text-surface scale-110'
                      : 'bg-primary/20 dark:bg-primary/30 text-primary border border-primary/20 hover:bg-primary/40'
                      }`}
                  >
                    {p}
                  </button>
                ))}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= Math.ceil(filteredBooks.length / ITEMS_PER_PAGE)}
                className="w-12 h-12 flex items-center justify-center bg-primary/20 dark:bg-primary/30 text-primary border border-primary/20 rounded-2xl disabled:opacity-20 disabled:cursor-not-allowed hover:bg-primary/40 transition-all"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          )}
        </div>
      )}
      </WatchdogGuard>

      {/* Filter Modal */}
      <FilterModal 
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        languages={availableLanguages.map(l => ({ code: l, name: getLanguageName(l, t) }))}
        selectedLang={query}
        onSelectLang={setQuery}
        selectedLevel={selectedLevel}
        onSelectLevel={setSelectedLevel}
        sortBy={sortBy}
        onSelectSort={setSortBy}
        workType={workType}
        onSelectWorkType={setWorkType}
      />

      {selectedBook && (
        <BookDetailsModal
          book={selectedBook}
          onClose={() => setSelectedBook(null)}
          onImport={handleImport}
          onPreview={handlePreview}
          isImporting={isImporting}
          languageFlags={ISO_FLAG_MAP}
          renderCover={(b) => <BookCover book={{ ...selectedBook, ...b }} />}
          ratingAvg={ratingSummaries[String(selectedBook.id)]?.avg}
          ratingCount={ratingSummaries[String(selectedBook.id)]?.count}
        />
      )}
    </div>
  );
};

export default LibriTuiLibraryPage;
